import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import API_BASE_URL from "../../config";
import { useParams } from "react-router-dom";
import Popup from "../../../Popup";
import Table from "../../Table";
import { TableSkeleton } from "../Skeleton";
import { setSelectedMedium } from "../redux/boardQuestionSlice";
import { fetchMediums } from "../redux/mediumSlice";

const MediumClass = () => {
  const dispatch = useDispatch();

  /* ================= Redux Data ================= */
  const { boardId } = useParams();
  const mediums = useSelector((state) => state.mediums.list);

  const { selectedMedium } = useSelector((state) => state.boardQuestion);

  /* ================= Local State ================= */
  const [classes, setClasses] = useState([]);
  const [editingClass, setEditingClass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [className, setClassName] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({});

  // Link Subject state
  const [showLinkSubjectPopup, setShowLinkSubjectPopup] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedClassForLink, setSelectedClassForLink] = useState(null);
  /* ================= Initial Load ================= */

  // useEffect(() => {
  //   if (boardId) {
  //     fetchClasses({ boardId, pageNumber: page });
  //   }
  // }, [boardId, page]);
  useEffect(() => {
    if (boardId) {
      dispatch(fetchMediums(boardId));
    }
  }, [boardId, dispatch]);
  useEffect(() => {
    if (boardId) {
      fetchClasses({
        boardId,
        pageNumber: page,
        medium_id: selectedMedium || "",
        limit,
      });
    }
  }, [boardId, page, selectedMedium, limit]);
  const fetchClasses = async ({ boardId, pageNumber = 1, medium_id = "" }) => {
    try {
      setLoading(true);

      // let url = `${API_BASE_URL}/board/classes-by-board-medium?board_id=${boardId}&page=${pageNumber}&limit=${limit}&medium_id=${medium_id}`;
      const params = new URLSearchParams();

      if (boardId) params.append("board_id", boardId);
      if (pageNumber) params.append("page", pageNumber);
      if (limit) params.append("limit", limit);
      if (medium_id)
        params.append("medium_id", medium_id || selectedMedium || "");

      let url = `${API_BASE_URL}/board/classes-by-board-medium?${params.toString()}`;

      const res = await fetch(url);
      const data = await res.json();

      setClasses(data.data || []);
      setPagination(data.pagination || {});
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClass = async () => {
    if (!className.trim()) {
      alert("Please enter class name");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        board_id: boardId,
        ...(editingClass ? { updated_by: "1" } : { created_by: "1" }),
        ...(editingClass ? { class_name: className } : { name: className }),
      };
      console.log("Payload:", payload);
      if (editingClass) {
        await fetch(`${API_BASE_URL}/board/class-update/${editingClass}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await fetch(`${API_BASE_URL}/board/class-create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      setClassName("");
      setEditingClass(null);
      setShowPopup(false);
      fetchClasses({
        boardId,
        pageNumber: page,
        medium_id: selectedMedium,
        limit,
      });
    } catch (err) {
      console.error("Failed to save class", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";

    if (
      !window.confirm(
        `Are you sure you want to mark this class as "${newStatus}"?`,
      )
    )
      return;

    try {
      // await fetch(`${API_BASE_URL}/board/update-status/${id}`, {
      await fetch(`${API_BASE_URL}/board/update-classstatus/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      fetchClasses({
        boardId,
        pageNumber: page,
        medium_id: selectedMedium,
        limit,
      });
    } catch (err) {
      console.error("Failed to update status", err);
    }
  };

  const handleEdit = (cls) => {
    console.log("Editing class:", cls);
    setEditingClass(cls.class_id);
    setClassName(cls.class_name);
    setShowPopup(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this class?")) return;

    try {
      setLoading(true);
      const payload = {
        board_id: boardId,
        deleted_by: "1",
      };
      await fetch(`${API_BASE_URL}/board/class-delete/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setClassName("");
      setEditingClass(null);
      fetchClasses({
        boardId,
        pageNumber: page,
        medium_id: selectedMedium,
        limit,
      });
    } catch (err) {
      console.error("Failed to delete class", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Link Subject
  const handleLinkSubjectClick = async (classRow) => {
    setSelectedClassForLink(classRow);
    setSelectedSubjects([]);

    try {
      // Fetch all subjects for this board and medium
      const response = await fetch(
        `${API_BASE_URL}/board/subjects/by-medium/${classRow.medium_id}/${boardId}`,
      );
      const data = await response.json();
      setSubjects(data.data || []);

      // Fetch already linked subjects for this class
      try {
        const linkedResponse = await fetch(
          `${API_BASE_URL}/board/linked-subjects/${classRow.class_id}`,
        );
        const linkedData = await linkedResponse.json();
        const linkedSubjectIds = linkedData.data?.map((s) => s.id) || [];
        setSelectedSubjects(linkedSubjectIds);
      } catch (err) {
        console.log("No linked subjects found or API not available");
        setSelectedSubjects([]);
      }

      setShowLinkSubjectPopup(true);
    } catch (err) {
      console.error("Failed to fetch subjects", err);
      alert("Failed to fetch subjects");
    }
  };

  const handleSubjectToggle = (subjectId) => {
    setSelectedSubjects((prev) =>
      prev.includes(subjectId)
        ? prev.filter((id) => id !== subjectId)
        : [...prev, subjectId],
    );
  };

  const handleLinkSubjects = async () => {
    console.log("Selected Subjects:", selectedSubjects);
    console.log("Class:", selectedClassForLink);
    console.log("Board ID:", boardId);

    try {
      setLoading(true);
      const payload = {
        class_id: selectedClassForLink.class_id,
        subject_ids: selectedSubjects,
        created_by: 10,
      };

      console.log("Payload:", payload);

      const response = await fetch(`${API_BASE_URL}/board/sync-subjects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("API Response:", result);

      if (response.ok) {
        alert("Subjects linked successfully!");
        setShowLinkSubjectPopup(false);
        setSelectedSubjects([]);
        setSelectedClassForLink(null);
      } else {
        alert(
          "Failed to link subjects: " + (result.message || "Unknown error"),
        );
      }
    } catch (err) {
      console.error("Failed to link subjects", err);
      alert("Failed to link subjects");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = className.trim();

  // Define table columns
  const columns = [
    {
      header: "Class Name",
      accessor: "class_name",
      align: "left",
    },
    {
      header: "Medium Name",
      accessor: "medium_name",
      align: "left",
    },
    {
      header: "Status",
      accessor: "status",
      align: "center",
      render: (_, row) => {
        const isActive = row.status === "active" || row.status === 1;
        return (
          <span
            style={{
              display: "inline-block",
              padding: "4px 14px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              color: isActive ? "#15803d" : "#dc2626",
              backgroundColor: isActive ? "#dcfce7" : "#fee2e2",
              border: `1px solid ${isActive ? "#bbf7d0" : "#fecaca"}`,
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      header: "Actions",
      accessor: "actions",
      align: "center",
      render: (_, row) => {
        const isActive = row.status === "active" || row.status === 1;
        return (
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <i
              className="bi bi-pencil-square"
              style={{
                fontSize: "18px",
                color: "#3b82f6",
                cursor: "pointer",
              }}
              title="Edit"
              onClick={() => handleEdit(row)}
            />
            <div
              onClick={() => handleStatusToggle(row.class_id, row.status)}
              title={isActive ? "Deactivate" : "Activate"}
              style={{
                width: "44px",
                height: "24px",
                borderRadius: "12px",
                backgroundColor: isActive ? "#22c55e" : "#d1d5db",
                position: "relative",
                cursor: "pointer",
                transition: "background-color 0.3s",
              }}
            >
              <div
                style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  backgroundColor: "white",
                  position: "absolute",
                  top: "3px",
                  left: isActive ? "23px" : "3px",
                  transition: "left 0.3s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                }}
              />
            </div>
            <i
              className="bi bi-link-45deg"
              style={{
                fontSize: "20px",
                color: "#f59e0b",
                cursor: "pointer",
              }}
              title="Link Subject"
              onClick={() => handleLinkSubjectClick(row)}
            />
          </div>
        );
      },
    },
  ];

  /* ================= UI ================= */
  return (
    <div style={{ padding: "0", maxWidth: "100%", margin: "0 auto" }}>
      {/* HEADER */}
      <div
        style={{
          marginBottom: "24px",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "24px",
            fontWeight: "600",
            color: "#1f2937",
          }}
        >
          Classes
        </h2>
        <p
          style={{
            margin: "8px 0 0 0",
            fontSize: "14px",
            color: "#6b7280",
          }}
        >
          Classes are created from the Medium tab. Go to Medium tab and click
          "Add Class" for the desired medium.
        </p>
      </div>
      <div
        className="col-sm-6"
        style={{ marginBottom: "0", display: "flex", gap: "2%" }}
      >
        <label className="cascading-label">Medium</label>
        <select
          value={selectedMedium || ""}
          onChange={(e) => {
            dispatch(setSelectedMedium(e.target.value));
            setPage(1); // Reset pagination when medium changes
          }}
          // disabled={mediumsLoading || disabled || disabledValue}
          className="cascading-select"
        >
          <option value="">{"-- Select Medium --"}</option>
          {mediums.map((medium) => (
            <option key={medium.id} value={medium.id}>
              {medium.name}
            </option>
          ))}
        </select>
        <p>Total Records Count :{pagination?.totalRecords}</p>
      </div>
      <br />

      {/* TABLE */}
      {loading ? (
        <TableSkeleton rows={5} columns={3} />
      ) : (
        <Table
          columns={columns}
          data={classes}
          serialNumberOffset={(page - 1) * limit}
        />
      )}
      <div style={{ display: "flex", gap: "20%" }}>
        {/* {classes.length > 1 && ( */}
        {pagination?.totalRecords > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "left",
              alignItems: "left",
              gap: "10px",
              marginTop: "30px",
              flexWrap: "wrap",
            }}
          >
            <p>Items per Page</p>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {/* <option value="5">5</option> */}
              {/* <option value="10">10</option> */}
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        )}
        {pagination?.totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              marginTop: "30px",
              flexWrap: "wrap",
            }}
          >
            {/* PREVIOUS */}
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "none",
                background:
                  page === 1
                    ? "#e5e7eb"
                    : "linear-gradient(135deg, #6366f1, #3b82f6)",
                color: page === 1 ? "#9ca3af" : "white",
                cursor: page === 1 ? "not-allowed" : "pointer",
                boxShadow:
                  page === 1 ? "none" : "0 4px 10px rgba(59, 130, 246, 0.3)",
                transition: "all 0.3s ease",
              }}
            >
              ‹
            </button>

            {/* PAGE NUMBERS */}
            {Array.from(
              { length: pagination?.totalPages || 1 },
              (_, i) => i + 1,
            )
              .slice(
                Math.max(0, page - 3),
                Math.min(pagination?.totalPages || 1, page + 2),
              )
              .map((pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => setPage(pageNumber)}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    border: "none",
                    background:
                      pageNumber === page
                        ? "linear-gradient(135deg, #ec4899, #f43f5e)"
                        : "#f3f4f6",
                    color: pageNumber === page ? "white" : "#374151",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow:
                      pageNumber === page
                        ? "0 4px 12px rgba(244, 63, 94, 0.4)"
                        : "0 2px 6px rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                  }}
                >
                  {pageNumber}
                </button>
              ))}

            {/* NEXT */}
            <button
              disabled={page === pagination?.totalPages}
              onClick={() => setPage(page + 1)}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "none",
                background:
                  page === pagination?.totalPages
                    ? "#e5e7eb"
                    : "linear-gradient(135deg, #6366f1, #3b82f6)",
                color: page === pagination?.totalPages ? "#9ca3af" : "white",
                cursor:
                  page === pagination?.totalPages ? "not-allowed" : "pointer",
                boxShadow:
                  page === pagination?.totalPages
                    ? "none"
                    : "0 4px 10px rgba(59, 130, 246, 0.3)",
                transition: "all 0.3s ease",
              }}
            >
              ›
            </button>
          </div>
        )}
      </div>
      {/* POPUP */}
      {showPopup && (
        <Popup
          onClose={() => {
            setShowPopup(false);
            setClassName("");
            setEditingClass(null);
          }}
          title={editingClass ? "Edit Class" : "Add New Class"}
          maxWidth="400px"
        >
          {/* CLASS NAME */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Class Name <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              placeholder="Enter class name"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#111827",
                outline: "none",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
            />
          </div>

          {/* ACTIONS */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "16px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <button
              onClick={() => {
                setShowPopup(false);
                setClassName("");
                setEditingClass(null);
              }}
              style={{
                padding: "10px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                backgroundColor: "white",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f9fafb")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleAddClass}
              disabled={!isFormValid}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "white",
                backgroundColor: isFormValid ? "#3b82f6" : "#9ca3af",
                cursor: isFormValid ? "pointer" : "not-allowed",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => {
                if (isFormValid) e.target.style.backgroundColor = "#2563eb";
              }}
              onMouseOut={(e) => {
                if (isFormValid) e.target.style.backgroundColor = "#3b82f6";
              }}
            >
              {editingClass ? "Update Class" : "Save Class"}
            </button>
          </div>
        </Popup>
      )}

      {/* LINK SUBJECT POPUP */}
      {showLinkSubjectPopup && (
        <Popup
          onClose={() => {
            setShowLinkSubjectPopup(false);
            setSelectedSubjects([]);
            setSelectedClassForLink(null);
          }}
          title={`Link Subjects to ${selectedClassForLink?.class_name}`}
          maxWidth="500px"
        >
          {/* SUBJECTS LIST */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "12px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Select Subjects <span style={{ color: "#dc2626" }}>*</span>
            </label>
            {subjects.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "12px",
                  padding: "12px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  backgroundColor: "#f9fafb",
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {subjects.map((subject) => (
                  <label
                    key={subject.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      fontSize: "14px",
                      color: "#374151",
                      padding: "6px",
                      borderRadius: "4px",
                      transition: "background-color 0.15s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={selectedSubjects.includes(subject.id)}
                      onChange={() => handleSubjectToggle(subject.id)}
                      style={{
                        marginRight: "8px",
                        cursor: "pointer",
                        width: "16px",
                        height: "16px",
                      }}
                    />
                    {subject.subject_name}
                  </label>
                ))}
              </div>
            ) : (
              <p
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#6b7280",
                  fontSize: "14px",
                }}
              >
                No subjects found for this medium.
              </p>
            )}
          </div>

          {/* ACTIONS */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "16px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <button
              onClick={() => {
                setShowLinkSubjectPopup(false);
                setSelectedSubjects([]);
                setSelectedClassForLink(null);
              }}
              style={{
                padding: "10px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                backgroundColor: "white",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f9fafb")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleLinkSubjects}
              disabled={selectedSubjects.length === 0}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "white",
                backgroundColor:
                  selectedSubjects.length > 0 ? "#3b82f6" : "#9ca3af",
                cursor: selectedSubjects.length > 0 ? "pointer" : "not-allowed",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => {
                if (selectedSubjects.length > 0)
                  e.target.style.backgroundColor = "#2563eb";
              }}
              onMouseOut={(e) => {
                if (selectedSubjects.length > 0)
                  e.target.style.backgroundColor = "#3b82f6";
              }}
            >
              Link Subjects ({selectedSubjects.length})
            </button>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default MediumClass;
